import React, { useEffect, useMemo, useRef, useState } from 'react';
import LogoMark from './LogoMark';
import { ComposeIcon, MoreIcon, SearchIcon, SidebarIcon, TrashIcon } from './icons';

export default function Sidebar({
  conversations,
  currentId,
  currentUser,
  plan,
  creditBalance,
  paidUntil,
  creditResetAt,
  now = Date.now(),
  onManageKeys,
  isAdmin,
  onOpenAdmin,
  isOpen,
  onClose,
  onDelete,
  onLogout,
  onNewChat,
  onRename,
  onSelect,
}) {
  const [query, setQuery] = useState('');
  const [menuId, setMenuId] = useState('');
  const [editingId, setEditingId] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [actionError, setActionError] = useState('');
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const menuRef = useRef(null);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('vi');
    return normalized
      ? conversations.filter((item) =>
          (item.title || '').toLocaleLowerCase('vi').includes(normalized),
        )
      : conversations;
  }, [conversations, query]);

  useEffect(() => {
    const closeMenu = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuId('');
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  const beginRename = (item) => {
    setMenuId('');
    setEditingId(item.id);
    setDraftTitle(item.title || '');
    setActionError('');
  };
  const saveRename = async (id) => {
    const title = draftTitle.trim();
    if (!title) {
      setActionError('Tên cuộc trò chuyện không được để trống.');
      return;
    }
    try {
      await onRename(id, title);
      setEditingId('');
      setActionError('');
    } catch (err) {
      setActionError(err.message);
    }
  };
  const removeConversation = async (item) => {
    setMenuId('');
    if (
      !window.confirm(
        `Xóa cuộc trò chuyện “${item.title || 'Cuộc trò chuyện mới'}”? Hành động này không thể hoàn tác.`,
      )
    ) {
      return;
    }
    try {
      await onDelete(item.id);
      setActionError('');
    } catch (err) {
      setActionError(err.message);
    }
  };

  return (
    <>
      <div
        className={`sidebar-backdrop ${isOpen ? 'visible' : ''}`}
        onClick={onClose}
      />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <button className="brand-button" onClick={onNewChat}>
            <LogoMark size={25} />
            <span>Chatbox</span>
          </button>
          <button
            className="icon-button sidebar-close"
            onClick={onClose}
            aria-label="Thu gọn thanh bên"
            title="Thu gọn thanh bên"
          >
            <SidebarIcon />
          </button>
        </div>
        <nav className="sidebar-actions" aria-label="Điều hướng chính">
          <button onClick={onNewChat}>
            <ComposeIcon size={18} />
            <span>Đoạn chat mới</span>
          </button>
          <button onClick={onManageKeys}>
            <span aria-hidden="true">⌘</span>
            <span>API keys</span>
          </button>
        </nav>
        <div className="conversation-search">
          <SearchIcon size={17} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Tìm đoạn chat"
            aria-label="Tìm kiếm lịch sử trò chuyện"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="Xóa tìm kiếm">
              ×
            </button>
          )}
        </div>
        <div className="history-label">Đoạn chat</div>
        {actionError && (
          <div className="sidebar-error" role="alert">
            {actionError}
          </div>
        )}
        <div className="conversation-list">
          {!conversations.length && (
            <p className="empty-history">
              Các cuộc trò chuyện gần đây sẽ xuất hiện tại đây.
            </p>
          )}
          {!!conversations.length && !filtered.length && (
            <p className="empty-history">
              Không tìm thấy cuộc trò chuyện phù hợp.
            </p>
          )}
          {filtered.map((item) => (
            <div
              className={`conversation-entry ${item.id === currentId ? 'active' : ''}`}
              key={item.id}
            >
              {editingId === item.id ? (
                <input
                  className="rename-input"
                  value={draftTitle}
                  autoFocus
                  maxLength={100}
                  onChange={(event) => setDraftTitle(event.target.value)}
                  onBlur={() => saveRename(item.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      saveRename(item.id);
                    }
                    if (event.key === 'Escape') {
                      setEditingId('');
                      setActionError('');
                    }
                  }}
                />
              ) : (
                <button
                  className="conversation-select"
                  onClick={() => onSelect(item.id)}
                >
                  <span>{item.title || 'Cuộc trò chuyện mới'}</span>
                </button>
              )}
              {editingId !== item.id && (
                <div
                  className="conversation-menu-wrap"
                  ref={menuId === item.id ? menuRef : null}
                >
                  <button
                    className="conversation-more"
                    onClick={() =>
                      setMenuId(menuId === item.id ? '' : item.id)
                    }
                    aria-label={`Tùy chọn cho ${item.title}`}
                  >
                    <MoreIcon size={17} />
                  </button>
                  {menuId === item.id && (
                    <div className="conversation-menu">
                      <button onClick={() => beginRename(item)}>
                        <ComposeIcon size={16} />
                        Đổi tên
                      </button>
                      <button
                        className="danger"
                        onClick={() => removeConversation(item)}
                      >
                        <TrashIcon size={16} />
                        Xóa
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="account-wrap">
          <div className="account-row">
            <span className="avatar">
              {(currentUser.display_name || currentUser.username).charAt(0).toUpperCase()}
            </span>
            <span className="account-info">
              <span className="account-name">{currentUser.display_name || currentUser.username}</span>
              {plan && (
                <span className="account-plan">
                  {plan === 'paid' ? 'Paid' : 'Free'}
                </span>
              )}
            </span>
            <button className="logout-button" onClick={onLogout}>
              Logout
            </button>
          </div>
          <button type="button" className="account-profile-link" aria-expanded={showProfile} onClick={() => setShowProfile((current) => !current)}>Thông tin cá nhân</button>
          {showProfile && <div className="account-profile" aria-label="Thông tin cá nhân">
            <div><span>Họ và tên</span><strong>{currentUser.display_name || 'Chưa cập nhật'}</strong></div>
            <div><span>Họ</span><strong>{currentUser.last_name || 'Chưa cập nhật'}</strong></div>
            <div><span>Tên</span><strong>{currentUser.first_name || 'Chưa cập nhật'}</strong></div>
            <div><span>Email</span><strong>{currentUser.email || 'Chưa cập nhật'}</strong></div>
          </div>}
          <button type="button" className="account-details-toggle" aria-expanded={showAccountDetails} onClick={() => setShowAccountDetails((current) => !current)}>
            <span>{showAccountDetails ? 'Ẩn chi tiết gói' : 'Xem chi tiết gói'}</span><span aria-hidden="true">{showAccountDetails ? '⌃' : '⌄'}</span>
          </button>
          {showAccountDetails && <div className="account-details">
            <div><span>Credit còn lại</span><strong>{Number.isFinite(creditBalance) ? new Intl.NumberFormat('vi-VN').format(creditBalance) : '—'}</strong></div>
            {plan === 'paid' && paidUntil && <div><span>Thời gian còn lại</span><strong>{formatRemaining(Date.parse(paidUntil) - now)}</strong></div>}
            {creditResetAt && <div><span>Reset credit sau</span><strong>{formatRemaining(Date.parse(creditResetAt) - now)}</strong></div>}
          </div>}
          {isAdmin && <button type="button" className="admin-open-button" onClick={onOpenAdmin}>Trang quản trị</button>}
        </div>
      </aside>
    </>
  );
}

function formatRemaining(ms) {
  const minutes = Math.max(0, Math.ceil(ms / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days === 0) return `${hours} giờ ${mins} phút`;
  return `${days} ngày ${hours} giờ ${mins} phút`;
}
